import { useState, useEffect, useRef, useCallback } from 'react';
import i18n from '../../i18n';
import { auth } from '../utils/auth.js';
import { parseApiError } from '../utils/apiError.js';
import { domain as rawDomain } from '../utils/request.js';
import { WOPI_TOKEN, WOPI_TOKEN_FROM_SHARE, WOPI_DISCOVERY } from '../utils/urls.js';
import { env } from '@/shared/utils/env';

const domain = rawDomain.replace(/\/+$/, '');

/**
 * Hook to fetch and manage WOPI access tokens for Collabora Online.
 *
 * @param {number|string} fileId - The file ID to get a token for (with a shareToken: a file inside the linked folder, or the linked file)
 * @param {object} options
 * @param {boolean} options.enabled - Whether to fetch the token (default: true)
 * @param {boolean} options.readonly - Force readonly mode
 * @param {string}  options.shareToken - If provided, exchange a guest share token for a read-only WOPI token
 * @param {number}  options.version - Read-only preview of a historical file version
 * @returns {{ token, wopiSrc, collaboraUrl, loading, error, errorInfo, refresh }}
 *   errorInfo — {status, code} of a refused token request (status null for a
 *   network failure or timeout), so a caller can tell "no access" from a fault.
 */
export function useWopiToken(
  fileId,
  { enabled = true, readonly = false, shareToken = null, version = null } = {},
) {
  const [token, setToken] = useState(null);
  const [tokenTtl, setTokenTtl] = useState(null); // seconds
  const [wopiSrc, setWopiSrc] = useState(null);
  const [collaboraUrl, setCollaboaUrl] = useState(null);
  const [editorPath, setEditorPath] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null);
  const refreshTimerRef = useRef(null);

  const fetchToken = useCallback(async () => {
    if (!enabled) return;
    if (!fileId && !shareToken) return;

    setLoading(true);
    setError(null);
    setErrorInfo(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      let url;
      let useAuthHeaders = true;
      if (shareToken) {
        // A file inside a folder link names itself; the backend opens only
        // what the link reaches.
        url = `${domain}/${WOPI_TOKEN_FROM_SHARE}?share_token=${encodeURIComponent(shareToken)}${fileId ? `&file_id=${fileId}` : ''}`;
        useAuthHeaders = false;
      } else {
        const params = new URLSearchParams();
        if (readonly) params.set('readonly', 'true');
        if (version != null) params.set('version', String(version));
        const qs = params.toString();
        url = `${domain}/${WOPI_TOKEN.replace('{id}', fileId)}${qs ? `?${qs}` : ''}`;
      }

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          ...(useAuthHeaders ? auth().headers : {}),
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        const parsed = parseApiError({ status: resp.status, data: body });
        throw Object.assign(new Error(`Failed to get WOPI token: ${resp.status}`), {
          info: { status: resp.status, code: parsed.code || null },
        });
      }

      const data = await resp.json();
      setToken(data.access_token);
      setWopiSrc(data.wopi_src);
      setTokenTtl(data.access_token_ttl || null);

      // Schedule token refresh at 80% of TTL
      if (data.access_token_ttl) {
        const refreshMs = data.access_token_ttl * 800; // 80% of TTL in ms
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(fetchToken, refreshMs);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('[useWopiToken] Error:', err);
      setError(err.name === 'AbortError' ? i18n.t('editor:requestTimeout') : err.message);
      setErrorInfo(err.info || { status: null, code: null });
    } finally {
      setLoading(false);
    }
  }, [fileId, enabled, readonly, shareToken, version]);

  // Fetch Collabora editor URL from discovery
  useEffect(() => {
    if (!enabled) return;

    const fetchDiscovery = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const discoveryUrl = `${domain}/${WOPI_DISCOVERY}`;
        const resp = await fetch(discoveryUrl, { headers: auth().headers, signal: controller.signal });
        clearTimeout(timeoutId);

        if (!resp.ok) {
          console.warn('[useWopiToken] Discovery fetch failed, using fallback URL');
          // Fallback: construct URL from env or default
          const collaboraDomain = env('VITE_COLLABORA_URL') || `${window.location.protocol}//collabora.${window.location.hostname}`;
          setCollaboaUrl(collaboraDomain);
          return;
        }

        const xml = await resp.text();
        // We only need the COOL base URL — every file extension routes through
        // the same `/browser/dist/cool.html`. Collabora picks the right app
        // (Writer / Calc / Impress) from the BaseFileName our WOPI metadata
        // returns. So grab any edit action; docx is just a likely-present hint.
        const parser = new DOMParser();
        const doc = parser.parseFromString(xml, 'application/xml');
        const actions = doc.querySelectorAll('action');
        let editorUrl = null;

        for (const action of actions) {
          const ext = action.getAttribute('ext');
          const name = action.getAttribute('name');
          if (ext === 'docx' && name === 'edit') {
            editorUrl = action.getAttribute('urlsrc');
            break;
          }
        }

        if (!editorUrl) {
          for (const action of actions) {
            if (action.getAttribute('name') === 'edit') {
              editorUrl = action.getAttribute('urlsrc');
              break;
            }
          }
        }

        if (editorUrl) {
          // Extract base URL (remove query params template)
          const baseUrl = editorUrl.split('?')[0];
          // Extract the Collabora host from the URL
          const urlObj = new URL(baseUrl);
          setCollaboaUrl(urlObj.origin);
          setEditorPath(urlObj.pathname);
        } else {
          const collaboraDomain = env('VITE_COLLABORA_URL') || `${window.location.protocol}//collabora.${window.location.hostname}`;
          setCollaboaUrl(collaboraDomain);
        }
      } catch (err) {
        clearTimeout(timeoutId);
        console.warn('[useWopiToken] Discovery error, using fallback:', err);
        const collaboraDomain = env('VITE_COLLABORA_URL') || `${window.location.protocol}//collabora.${window.location.hostname}`;
        setCollaboaUrl(collaboraDomain);
      }
    };

    fetchDiscovery();
  }, [enabled]);

  // Fetch token when fileId changes
  useEffect(() => {
    fetchToken();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [fetchToken]);

  return {
    token,
    tokenTtl,
    wopiSrc,
    collaboraUrl,
    editorPath,
    loading,
    error,
    errorInfo,
    refresh: fetchToken,
  };
}
